import SelfHealingTargets from './SelfHealingTargets'
import ProjectMonitoringSetup from './ProjectMonitoringSetup'
import { useState } from 'react'
import type { z } from 'zod'
import { CheckCircle2, CircleDashed } from 'lucide-react'
import { useGetMonitoringStatusQuery } from '../services/sessionMonitoringApi'
import { useGetSessionReviewersQuery, useUpdateSessionReviewerMutation } from '../services/sessionReviewersApi'
import { ReviewerSettingsSchema, type ReviewerCardSchema } from '../schemas/sessionReviewers'
import SessionSummarizerSettings from './SessionSummarizerSettings'
import { Link } from './nav'
import './TestingSetup.css'

type Reviewer = z.infer<typeof ReviewerCardSchema>

function ReviewerSettings({ projectId, reviewer, canEdit, setup }: { projectId: string; reviewer: Reviewer; canEdit: boolean; setup: boolean }) {
  const [settings, setSettings] = useState(reviewer.settings)
  const [prompt, setPrompt] = useState(reviewer.prompt ?? reviewer.default_prompt ?? '')
  const [save, request] = useUpdateSessionReviewerMutation()
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  async function submit(kind: 'settings' | 'prompt') {
    setError(''); setMessage('')
    if (kind === 'settings') {
      const parsed = ReviewerSettingsSchema.safeParse(settings)
      if (!parsed.success) { setError(parsed.error.issues[0]?.message ?? 'Check the review limits.'); return }
    }
    try {
      await save({ projectId, body: kind === 'settings' ? { action: 'settings', reviewer: reviewer.key, settings } : { action: 'prompt', reviewer: reviewer.key, prompt } }).unwrap()
      setMessage(kind === 'settings' ? 'Reviewer settings saved.' : 'Reviewer instructions saved.')
    } catch { setError('Could not save this reviewer. Please try again.') }
  }
  return <details className="self-healing__reviewer">
    <summary><span>{reviewer.name}</span><span className="self-healing__badge">{reviewer.settings.enabled ? 'Enabled' : 'Disabled'}</span></summary>
    <p>{reviewer.goal}</p>
    <form className="self-healing__form" onSubmit={event => { event.preventDefault(); void submit('settings') }}>
      <fieldset disabled={!canEdit || request.isLoading}>
        <label className="self-healing__toggle"><input type="checkbox" checked={settings.enabled} onChange={e => setSettings({ ...settings, enabled: e.target.checked })} /> Review new sessions automatically</label>
        <label className="self-healing__toggle"><input type="checkbox" checked={settings.create_journeys} onChange={e => setSettings({ ...settings, create_journeys: e.target.checked })} /> Create reproduction journeys for findings</label>
        <label>Wait after session activity (minutes)<input type="number" required min="1" max="1440" value={settings.quiet_minutes} onChange={e => setSettings({ ...settings, quiet_minutes: e.target.valueAsNumber })} /></label>
        {canEdit && <button className="testing-setup__primary" type="submit">{request.isLoading ? 'Saving…' : 'Save reviewer'}</button>}
      </fieldset>
    </form>
    {!setup && <details><summary>Reviewer instructions</summary><form className="self-healing__form" onSubmit={event => { event.preventDefault(); void submit('prompt') }}>
      <label>Instructions<textarea required maxLength={20000} rows={7} value={prompt} disabled={!canEdit} onChange={e => setPrompt(e.target.value)} /></label>
      {canEdit && <button type="submit" disabled={request.isLoading}>Save instructions</button>}
    </form></details>}
    {error && <p role="alert">{error}</p>}{message && <p role="status">{message}</p>}
    {!setup && <Link to={`/projects/${projectId}/session-reviewers/${reviewer.key}`}>View review results</Link>}
  </details>
}

export default function SelfHealingSettings({ projectId, setup = false }: { projectId: string; setup?: boolean }) {
  const statusQuery = useGetMonitoringStatusQuery(projectId, { pollingInterval: 15000, skipPollingIfUnfocused: true })
  const status = statusQuery.currentData
  const showReviewers = setup || (status?.configured === true && status.has_sessions)
  const reviewers = useGetSessionReviewersQuery(projectId, { skip: !showReviewers })
  return <div className="self-healing testing-settings">
    <section>
      <h3>Watch real sessions for bugs</h3>
      <p>QA reviews your app’s user sessions for bugs. Reviewers can turn findings into reproduction journeys, and daily reports on Overview summarize the results. Sessions and review results appear together in daily Test Runs.</p>
      {statusQuery.isLoading && <p role="status">Checking connection…</p>}
      {statusQuery.isError && <p role="alert">Could not check the session connection. <button onClick={() => void statusQuery.refetch()}>Retry</button></p>}
      {status && <p className="self-healing__connection" role="status">{status.has_sessions ? <CheckCircle2 size={18} aria-hidden /> : <CircleDashed size={18} aria-hidden />}{status.has_sessions ? 'Sessions received' : status.configured ? 'Credential configured · waiting for the first session' : 'Sessions not connected'}</p>}
      {status?.has_sessions && <p>Sessions are connected. Enabled reviewers will analyze new sessions, create reproduction journeys for findings, and include results in your daily report. Manage reviewers, daily targets, and reports below.</p>}
      {status?.can_edit && <ProjectMonitoringSetup projectId={projectId} />}
    </section>
    <SelfHealingTargets projectId={projectId} />
    {showReviewers && <section>
      <h3>Session reviewers</h3>
      <p>Choose which reviewers run automatically and whether they create reproduction journeys. Saved reviewers start processing when sessions arrive.</p>
      {reviewers.isLoading && <p role="status">Loading reviewers…</p>}
      {reviewers.isError && <p role="alert">Could not load reviewers. <button onClick={() => void reviewers.refetch()}>Retry</button></p>}
      {reviewers.currentData?.reviewers.map(reviewer => <ReviewerSettings key={reviewer.key} projectId={projectId} reviewer={reviewer} canEdit={reviewers.currentData?.can_edit === true} setup={setup} />)}
    </section>}
    {showReviewers && <SessionSummarizerSettings projectId={projectId} />}
  </div>
}
